import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { PageHeader, TableSkeleton } from '@/components/shared/page';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDate, formatMoney } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useProfitAndLoss, useReceivablesAgeing, useTrialBalance } from './api';
import { AGEING_LABELS } from './labels';

const TABS = [
  ['ageing', 'Receivables ageing'],
  ['pnl', 'Profit and loss'],
  ['trial', 'Trial balance'],
] as const;

export function FinanceReportsPage() {
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') as (typeof TABS)[number][0]) ?? 'ageing';

  const now = new Date();
  const [from, setFrom] = useState(new Date(now.getFullYear(), 0, 1).toISOString().slice(0, 10));
  const [to, setTo] = useState(now.toISOString().slice(0, 10));

  return (
    <div>
      <PageHeader
        title="Financial reports"
        description="Everything here is derived from the ledger, so it can always be traced back."
      />

      <div className="mb-5 flex flex-wrap items-end justify-between gap-3 border-b pb-3">
        <div className="flex gap-1">
          {TABS.map(([key, label]) => (
            <button
              key={key}
              onClick={() => setParams({ tab: key })}
              className={
                tab === key
                  ? 'border-b-2 border-primary px-3 py-2 text-sm font-medium'
                  : 'px-3 py-2 text-sm text-muted-foreground hover:text-foreground'
              }
            >
              {label}
            </button>
          ))}
        </div>

        {tab !== 'ageing' && (
          <div className="flex items-end gap-2">
            <div className="space-y-1">
              <Label className="text-xs">From</Label>
              <Input
                type="date"
                className="h-8"
                value={from}
                onChange={(event) => setFrom(event.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">To</Label>
              <Input
                type="date"
                className="h-8"
                value={to}
                onChange={(event) => setTo(event.target.value)}
              />
            </div>
          </div>
        )}
      </div>

      {tab === 'ageing' && <AgeingReport />}
      {tab === 'pnl' && <ProfitAndLossReport from={from} to={to} />}
      {tab === 'trial' && <TrialBalanceReport from={from} to={to} />}
    </div>
  );
}

function AgeingReport() {
  const { data, isLoading } = useReceivablesAgeing();
  if (isLoading) return <TableSkeleton columns={6} />;
  if (!data || data.rows.length === 0) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm text-muted-foreground">
          Nothing outstanding. Everything invoiced has been paid.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Who owes what, and for how long</CardTitle>
        <CardDescription>
          Buckets run from the due date, not the issue date.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Client</TableHead>
              {data.buckets.map((bucket) => (
                <TableHead key={bucket} className="text-right">
                  {AGEING_LABELS[bucket] ?? bucket}
                </TableHead>
              ))}
              <TableHead className="text-right">Total</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.rows.map((row) => (
              <TableRow key={row.companyId}>
                <TableCell className="font-medium">{row.companyName}</TableCell>
                {data.buckets.map((bucket) => (
                  <TableCell
                    key={bucket}
                    className={cn(
                      'tabular text-right',
                      bucket === '90+' &&
                        row.buckets[bucket] !== '0.00' &&
                        'font-medium text-destructive',
                    )}
                  >
                    {row.buckets[bucket] === '0.00' ? '—' : formatMoney(row.buckets[bucket])}
                  </TableCell>
                ))}
                <TableCell className="tabular text-right font-semibold">
                  {formatMoney(row.total)}
                </TableCell>
              </TableRow>
            ))}
            <TableRow className="border-t-2 hover:bg-transparent">
              <TableCell className="font-semibold">Total</TableCell>
              {data.buckets.map((bucket) => (
                <TableCell key={bucket} className="tabular text-right font-semibold">
                  {formatMoney(data.totals[bucket])}
                </TableCell>
              ))}
              <TableCell />
            </TableRow>
          </TableBody>
        </Table>

        <div className="mt-5 space-y-3">
          {data.rows.map((row) => (
            <details key={row.companyId} className="rounded-md border px-3 py-2">
              <summary className="cursor-pointer text-sm">
                {row.companyName} — {row.invoices.length}{' '}
                {row.invoices.length === 1 ? 'invoice' : 'invoices'}
              </summary>
              <ul className="mt-2 space-y-1">
                {row.invoices.map((invoice) => (
                  <li key={invoice.id} className="flex items-center justify-between text-sm">
                    <Link
                      to={`/finance/invoices/${invoice.id}`}
                      className="text-primary hover:underline"
                    >
                      {invoice.number}
                    </Link>
                    <span className="flex items-center gap-3 text-muted-foreground">
                      <span className="text-xs">due {formatDate(invoice.dueDate)}</span>
                      <span className="tabular">{formatMoney(invoice.balanceDue)}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </details>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function ProfitAndLossReport({ from, to }: { from: string; to: string }) {
  const { data, isLoading } = useProfitAndLoss({ from, to });
  if (isLoading) return <TableSkeleton columns={3} />;
  if (!data) return null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Profit and loss</CardTitle>
        <CardDescription>
          {formatDate(data.from)} to {formatDate(data.to)}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <Section title="Income" lines={data.income} total={data.totalIncome} />
        <Section title="Expenses" lines={data.expense} total={data.totalExpense} />

        <div className="flex items-center justify-between border-t-2 pt-3">
          <span className="text-lg font-semibold">Net profit</span>
          <span
            className={cn(
              'tabular text-lg font-semibold',
              data.netProfit.startsWith('-') && 'text-destructive',
            )}
          >
            {formatMoney(data.netProfit)}
          </span>
        </div>
        <div className="text-right text-sm text-muted-foreground">
          {data.marginPercent}% margin
        </div>
      </CardContent>
    </Card>
  );
}

function Section({
  title,
  lines,
  total,
}: {
  title: string;
  lines: { accountId: string; code: string; name: string; balance: string }[];
  total: string;
}) {
  return (
    <div>
      <h3 className="mb-2 text-sm font-semibold">{title}</h3>
      {lines.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nothing recorded in this period.</p>
      ) : (
        <ul className="space-y-1.5">
          {lines.map((line) => (
            <li key={line.accountId} className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                <span className="mr-2 font-mono text-xs">{line.code}</span>
                {line.name}
              </span>
              <span className="tabular">{formatMoney(line.balance)}</span>
            </li>
          ))}
        </ul>
      )}
      <div className="mt-2 flex items-center justify-between border-t pt-2 text-sm font-medium">
        <span>Total {title.toLowerCase()}</span>
        <span className="tabular">{formatMoney(total)}</span>
      </div>
    </div>
  );
}

function TrialBalanceReport({ from, to }: { from: string; to: string }) {
  const { data, isLoading } = useTrialBalance({ from, to });
  if (isLoading) return <TableSkeleton columns={4} />;
  if (!data) return null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          Trial balance
          {data.balanced ? (
            <span className="flex items-center gap-1 text-xs font-normal text-emerald-600">
              <CheckCircle2 className="h-3.5 w-3.5" />
              Balanced
            </span>
          ) : (
            <span className="flex items-center gap-1 text-xs font-normal text-destructive">
              <AlertTriangle className="h-3.5 w-3.5" />
              Out of balance
            </span>
          )}
        </CardTitle>
        <CardDescription>
          Debits and credits across every account. If these ever differ, something posted outside
          the ledger service.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {data.lines.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            Nothing posted in this period yet.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Account</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="text-right">Debit</TableHead>
                <TableHead className="text-right">Credit</TableHead>
                <TableHead className="text-right">Balance</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.lines.map((line) => (
                <TableRow key={line.accountId}>
                  <TableCell>
                    <span className="mr-2 font-mono text-xs text-muted-foreground">
                      {line.code}
                    </span>
                    {line.name}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{line.type}</TableCell>
                  <TableCell className="tabular text-right">
                    {line.debit === '0.00' ? '—' : formatMoney(line.debit)}
                  </TableCell>
                  <TableCell className="tabular text-right">
                    {line.credit === '0.00' ? '—' : formatMoney(line.credit)}
                  </TableCell>
                  <TableCell className="tabular text-right font-medium">
                    {formatMoney(line.balance)}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow className="border-t-2 hover:bg-transparent">
                <TableCell colSpan={2} className="font-semibold">
                  Totals
                </TableCell>
                <TableCell className="tabular text-right font-semibold">
                  {formatMoney(data.totalDebit)}
                </TableCell>
                <TableCell className="tabular text-right font-semibold">
                  {formatMoney(data.totalCredit)}
                </TableCell>
                <TableCell />
              </TableRow>
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
